import { ChangeEvent, FocusEvent, useState } from 'react';
import { Control, FieldValues, Path, useController } from 'react-hook-form';
import styled from 'styled-components';

import { Input } from '@/components/Forms';
import Text from '@/components/Text';
import useDidUpdate from '@/hooks/useDidUpdate';
import { ensureStringContainsPositiveDigitsOnly } from '@/utils/helpers';

const InputGroup = styled.div`
  position: relative;
`;

const InputLabel = styled(Text)`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 40px;
  height: 24px;
  margin: auto 0;
`;

const Buttons = styled.div`
  position: absolute;
  width: 46px;
  right: 0;
  top: 0;
  bottom: 0;
  height: 100%;
  border-left: 1px solid ${({ theme }) => theme.colors.grayscale};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  transition: 0.2s ease-out border-color;
`;

const ArrowButton = styled.button`
  width: 8px;
  height: 5px;
  padding: 3px;
  box-sizing: content-box;
  display: flex;
`;

const StyledInput = styled(Input)`
  &:focus,
  &:hover {
    + ${Buttons} {
      border-color: ${({ theme }) => theme.colors.gray};
    }
  }
`;

const Wrap = styled.div`
  gap: 24px;
  display: flex;
`;

const StyledInputGroup = styled(InputGroup)`
  flex: 1;
`;

interface Props<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  minutesOnly?: boolean;
}

const DurationField = <T extends FieldValues>({
  control,
  name,
  minutesOnly = false,
}: Props<T>) => {
  const {
    field: { onChange, value },
  } = useController({
    name,
    control,
  });

  const [values, setValues] = useState(() => {
    if (!value) {
      return {
        min: '0',
        sec: '0',
      };
    }
    const min = Math.floor(value / 60);
    const sec = value - min * 60;

    return {
      min: min.toString(),
      sec: sec.toString(),
    };
  });

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!ensureStringContainsPositiveDigitsOnly(e.target.value)) return;

    setValues((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const increment = (key: keyof typeof values) => {
    setValues((prev) => {
      const currentValue = prev[key] || '0';

      const newValue = parseInt(currentValue) + 1;

      return { ...prev, [key]: newValue.toString() };
    });
  };

  const decrement = (key: keyof typeof values) => {
    setValues((prev) => {
      const currentValue = parseInt(prev[key] || '0');

      const newValue = currentValue > 0 ? currentValue - 1 : 0;

      return { ...prev, [key]: newValue.toString() };
    });
  };

  const onBlur = (e: FocusEvent<HTMLInputElement>) => {
    const value = values[e.target.name as keyof typeof values];

    if (value) return;

    setValues((prev) => ({ ...prev, [e.target.name]: '0' }));
  };

  useDidUpdate(() => {
    const minutes = parseInt(values.min || '0');
    const seconds = parseInt(values.sec || '0');

    onChange(minutes * 60 + seconds);
  }, [values]);

  return (
    <Wrap>
      <StyledInputGroup>
        <StyledInput
          onChange={onInputChange}
          name="min"
          value={values.min}
          onBlur={onBlur}
        />
        <Buttons>
          <ArrowButton type="button" onClick={() => increment('min')}>
            <svg
              width="8"
              height="5"
              viewBox="0 0 8 5"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M4 0L0 5H8L4 0Z" fill="currentColor" />
            </svg>
          </ArrowButton>
          <ArrowButton type="button" onClick={() => decrement('min')}>
            <svg
              width="8"
              height="5"
              viewBox="0 0 8 5"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M4 5L8 0H0L4 5Z" fill="currentColor" />
            </svg>
          </ArrowButton>
        </Buttons>
        <InputLabel $color="gray" $type="caption" as="span">
          min
        </InputLabel>
      </StyledInputGroup>
      {!minutesOnly && (
        <StyledInputGroup>
          <StyledInput
            onChange={onInputChange}
            name="sec"
            value={values.sec}
            onBlur={onBlur}
          />
          <Buttons>
            <ArrowButton type="button" onClick={() => increment('sec')}>
              <svg
                width="8"
                height="5"
                viewBox="0 0 8 5"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M4 0L0 5H8L4 0Z" fill="currentColor" />
              </svg>
            </ArrowButton>
            <ArrowButton type="button" onClick={() => decrement('sec')}>
              <svg
                width="8"
                height="5"
                viewBox="0 0 8 5"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M4 5L8 0H0L4 5Z" fill="currentColor" />
              </svg>
            </ArrowButton>
          </Buttons>
          <InputLabel $color="gray" $type="caption" as="span">
            sec
          </InputLabel>
        </StyledInputGroup>
      )}
    </Wrap>
  );
};

export default DurationField;
